import {IconButton} from '@dropbox/dig-components/buttons';
import {Modal} from '@dropbox/dig-components/modal';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Cluster, Stack} from '@dropbox/dig-foundations';
import type {ScorecardWorkstreamContextOut} from 'client';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {StatusButtonIcon} from 'components/DSYS/StatusButtonIcon';
import {StatusChip} from 'components/DSYS/StatusChip';
import {t} from 'i18next';
import {useEffect, useState} from 'react';
import {fromServerDate} from 'views/admin/utils';
import {getWorkstreamUpdateVisualStatus} from 'views/workstreams/utils';

import styles from './ScorecardWorkstreamStatusBadge.module.css';

export const ScorecardWorkstreamStatusBadge = ({
  context,
  snapshotKey,
}: {
  context?: ScorecardWorkstreamContextOut | null;
  snapshotKey?: string | null;
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [snapshotKey, context?.id]);

  if (!context || context.show_status_badge === false) {
    return null;
  }

  const {update} = context;
  const status = update?.status ?? 'no_status';
  const visualStatus = getWorkstreamUpdateVisualStatus(status);
  const label = t('scorecard_workstream_status_label', {
    name: context.name,
    status: t(status),
  });
  const historicalDate = snapshotKey && snapshotKey !== 'current' ? snapshotKey : undefined;

  return (
    <Box
      tagName="span"
      display="inline-flex"
      flexShrink={0}
      className={styles.badge}
      onClick={(event: React.MouseEvent<HTMLSpanElement>) => event.stopPropagation()}
      onKeyDown={(event: React.KeyboardEvent<HTMLSpanElement>) => event.stopPropagation()}
    >
      <IconButton
        size="small"
        variant="borderless"
        aria-label={label}
        aria-haspopup="dialog"
        className={styles.badgeButton}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          setOpen(true);
        }}
      >
        <StatusButtonIcon status={visualStatus} size="xsmall" includeArchived />
      </IconButton>
      {/* Keep mounted so DIG can finish closing and restore app accessibility. */}
      <Modal
        open={open}
        width={1000}
        className={styles.modal}
        onRequestClose={() => setOpen(false)}
        withCloseButton={t('close')}
        aria-label={context.name}
      >
        <Modal.Header className={styles.modalHeader}>
          <Modal.Title
            size="small"
            className={`${atoms({display: 'flex', alignItems: 'center', columnGap: '8'})} ${styles.modalTitle}`}
          >
            <StatusButtonIcon status={visualStatus} size="small" includeArchived />
            <Box tagName="span">{context.name}</Box>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <Stack gap="16">
            <Cluster alignY="center" gap="8">
              <StatusChip status={status} asStatus={visualStatus} size="small" includeArchived />
              {update && (
                <Text size="small" color="subtle">
                  <time dateTime={update.created_at}>
                    {fromServerDate(update.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      timeZone: 'UTC',
                    })}
                  </time>
                </Text>
              )}
            </Cluster>
            {update ? (
              <RichTextArea
                source="scorecard-workstream-update"
                value={update.comment}
                editable={false}
                paddingX="0"
                hideCommentHighlights
                disableAISuggestions
              />
            ) : (
              <Text>
                {historicalDate
                  ? t('scorecard_workstream_no_update_as_of', {date: historicalDate})
                  : t('scorecard_workstream_no_update')}
              </Text>
            )}
          </Stack>
        </Modal.Body>
      </Modal>
    </Box>
  );
};
